/**
 * THE 1:1 CALL DIAG'S DOORBELL — the only part of the call diagnostic that a
 * production bundle carries, and it carries it as nothing.
 *
 * The sibling of `components/circles/videoDiagGate.ts`, for the direct WebRTC
 * 1:1 (`utils/PeerConnection.ts` + `components/empathy/PeerAudioRoom.tsx`).
 * Those two ring it at the moments only they can see — a signalling message in
 * or out, a negotiation started, skipped, abandoned or sent, a glare decided —
 * and `utils/callDiag.ts`, which `PeerAudioRoom` imports dynamically behind
 * `DEBUG_SURFACES_ENABLED`, is the one sink. On a production build no sink is
 * ever registered and this returns at its first line.
 *
 * Why a sink and not a direct import: a static import of the diagnostic from
 * the connection would put the snapshot builder into every bundle that has a
 * call in it, gated or not.
 */

import { DEBUG_SURFACES_ENABLED } from './debugSurfaces';

export type CallDiagSink = (event: string, detail?: Record<string, unknown>) => void;

let sink: CallDiagSink | null = null;

/** Registered by `callDiag.ts` while it runs. */
export function setCallDiagSink(next: CallDiagSink | null): void {
  sink = next;
}

/**
 * Unregister `mine`, and only `mine`: a call screen that remounts starts its
 * second diagnostic before the first one's cleanup runs, and a blind
 * `setCallDiagSink(null)` there would silence the one still running.
 */
export function releaseCallDiagSink(mine: CallDiagSink): void {
  if (sink === mine) sink = null;
}

/**
 * Tell the running diagnostic, if there is one, that something happened.
 * `detail` is handed over by reference and summarised by the sink, so a
 * production caller pays for nothing but this function's first line.
 */
export function noteCallDiag(event: string, detail?: Record<string, unknown>): void {
  if (!DEBUG_SURFACES_ENABLED) return;
  try {
    sink?.(event, detail);
  } catch {
    /* diagnostic only — never into the call path */
  }
}
